import React, { useState } from 'react'
import { Button } from '@progress/kendo-react-buttons'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import { FormInput, FormNumericTextBox } from '../FormComps/FormComponents'
import { LabourWorking } from '../../data/interfaces'
import {
  createLabourWorking,
  updateLabourWorking,
  deleteLabourWorking
} from '../../data/workingsData'
import { useToast } from '../../contexts/ToastContext'
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa'
import { labourWorkingTotal, inSection } from './workingTotals'

interface LabourWorkingsTableProps {
  labourWorkings: LabourWorking[]
  enquiryId?: number
  quoteId?: number
  jobId?: number
  sectionId?: number
  lockEdit: boolean
  onLabourWorkingsChange: (workings: LabourWorking[]) => void
}

const LabourWorkingsTable: React.FC<LabourWorkingsTableProps> = ({
  labourWorkings,
  enquiryId,
  quoteId,
  jobId,
  sectionId,
  lockEdit,
  onLabourWorkingsChange
}) => {
  const { showToast } = useToast()
  const [dialogVisible, setDialogVisible] = useState(false)
  const [editingItem, setEditingItem] = useState<LabourWorking | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const items = labourWorkings.filter(w => inSection(w.sectionId, sectionId))

  const handleAdd = () => {
    setEditingItem({
      enquiryId,
      quoteId,
      jobId,
      sectionId,
      description: '',
      hours: 0,
      rate: 0,
      type: ''
    })
    setDialogVisible(true)
  }

  const handleEdit = (item: LabourWorking) => {
    setEditingItem({ ...item })
    setDialogVisible(true)
  }

  const handleDelete = async (item: LabourWorking) => {
    if (!item.labourWorkingId) return
    if (window.confirm('Are you sure you want to delete this labour working?')) {
      setIsLoading(true)
      try {
        await deleteLabourWorking(item.labourWorkingId)
        onLabourWorkingsChange(labourWorkings.filter(w => w.labourWorkingId !== item.labourWorkingId))
        showToast('Labour working deleted successfully', 'success')
      } catch (error) {
        console.error('Error deleting labour working:', error)
        showToast('Error deleting labour working', 'error')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleSave = async (formData: LabourWorking) => {
    setIsLoading(true)
    try {
      const totalCost = (formData.hours || 0) * (formData.rate || 0)
      const workingData = {
        ...formData,
        labourWorkingId: editingItem?.labourWorkingId,
        enquiryId,
        quoteId,
        jobId,
        sectionId: editingItem?.sectionId,
        totalCost: totalCost
      }

      if (editingItem?.labourWorkingId) {
        const updated = await updateLabourWorking(editingItem.labourWorkingId, workingData)
        onLabourWorkingsChange(labourWorkings.map(w =>
          w.labourWorkingId === editingItem.labourWorkingId ? updated : w
        ))
        showToast('Labour working updated successfully', 'success')
      } else {
        const created = await createLabourWorking(workingData)
        onLabourWorkingsChange([...labourWorkings, created])
        showToast('Labour working created successfully', 'success')
      }

      setDialogVisible(false)
      setEditingItem(null)
    } catch (error) {
      console.error('Error saving labour working:', error)
      showToast('Error saving labour working', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="working-type-block">
      {items.length > 0 ? (
        <table className="workings-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Type</th>
              <th className="num">Hours</th>
              <th className="num">Rate</th>
              <th className="num">Total</th>
              <th className="actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.labourWorkingId}>
                <td>{item.description}</td>
                <td>{item.type}</td>
                <td className="num">{(item.hours || 0).toFixed(2)}</td>
                <td className="num">£{(item.rate || 0).toFixed(2)}</td>
                <td className="num">£{labourWorkingTotal(item).toFixed(2)}</td>
                <td className="actions">
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => handleEdit(item)} disabled={lockEdit || isLoading} title="Edit Labour Working">
                      <FaEdit />
                    </Button>
                    <Button size="small" fillMode="outline" themeColor="error" onClick={() => handleDelete(item)} disabled={lockEdit || isLoading} title="Delete Labour Working">
                      <FaTrash />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="working-section-empty">No labour workings yet.</div>
      )}

      {!lockEdit && (
        <div className="working-section-add">
          <Button size="small" themeColor="primary" fillMode="outline" onClick={handleAdd} disabled={isLoading}>
            <FaPlus style={{ marginRight: '0.35rem' }} /> Add Labour Working
          </Button>
        </div>
      )}

      {dialogVisible && editingItem && (
        <Dialog
          title={editingItem?.labourWorkingId ? 'Edit Labour Working' : 'Add Labour Working'}
          onClose={() => { setDialogVisible(false); setEditingItem(null) }}
          width={500}
        >
          <Form
            onSubmit={handleSave}
            initialValues={{
              description: editingItem.description || '',
              type: editingItem.type || '',
              hours: editingItem.hours || 0,
              rate: editingItem.rate || 0
            }}
            key={`labour-${editingItem?.labourWorkingId || 'new'}-${Date.now()}`}
            render={(formRenderProps) => (
              <FormElement style={{ maxWidth: 650 }}>
                <Field name="description" component={FormInput} label="Description" required />
                <Field name="type" component={FormInput} label="Type" />
                <Field name="hours" component={FormNumericTextBox} label="Hours" format="n2" min={0} max={10000} step={0.01} required />
                <Field name="rate" component={FormNumericTextBox} label="Rate (£)" format="n2" min={0} max={100000} step={0.01} required />
                <DialogActionsBar>
                  <Button type="submit" themeColor="primary" disabled={!formRenderProps.allowSubmit || isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                  </Button>
                  <Button onClick={() => { setDialogVisible(false); setEditingItem(null) }} disabled={isLoading}>
                    Cancel
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}
    </div>
  )
}

export default LabourWorkingsTable
